import axios, { AxiosError } from "axios";
import type { AxiosInstance, AxiosResponse } from "axios";
import { config, API_ENDPOINTS } from "../config/api";
import type {
  ProcessNoticeResponse,
  ZendeskTicketResponse,
  UserInfo,
  HealthCheckResponse,
} from "../types/notice";

// Create axios instances for each service
const createApiClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    timeout: config.timeout,
    headers: {
      Accept: "application/json",
    },
  });

  // Request interceptor for debugging in development
  client.interceptors.request.use(
    (config) => {
      if (import.meta.env.DEV) {
        console.log(
          `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
          {
            data: config.data,
            headers: config.headers,
          },
        );
      }
      return config;
    },
    (error) => {
      console.error("[API Request Error]", error);
      return Promise.reject(error);
    },
  );

  // Response interceptor for debugging and error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      if (import.meta.env.DEV) {
        console.log(
          `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
          {
            status: response.status,
            data: response.data,
          },
        );
      }
      return response;
    },
    (error: AxiosError) => {
      if (import.meta.env.DEV) {
        console.error(
          `[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          },
        );
      }
      return Promise.reject(error);
    },
  );

  return client;
};

// API clients
export const ocrApiClient = createApiClient(config.ocrServiceUrl);
export const zendeskApiClient = createApiClient(config.zendeskServiceUrl);


// Error handling utility
export const handleApiError = (error: unknown): string => {
  if (error instanceof AxiosError) {
    if (error.response?.data) {
      const errorData = error.response.data as any;

      // Handle 422 validation errors with detail array
      if ("detail" in errorData) {
        if (Array.isArray(errorData.detail)) {
          // Extract error messages from validation error array
          const messages = errorData.detail.map((err: any) => {
            if (typeof err === "object" && err.msg) {
              const field =
                err.loc && err.loc.length > 1
                  ? err.loc[err.loc.length - 1]
                  : "field";
              return `${field}: ${err.msg}`;
            }
            return String(err);
          });
          return messages.join(", ");
        }
        return String(errorData.detail);
      }

      if ("error" in errorData) {
        return String(errorData.error);
      }
    }

    if (error.code === "ECONNABORTED") {
      return "Request timeout. Please try again.";
    }

    if (error.code === "ERR_NETWORK") {
      return "Network error. Please check your connection and try again.";
    }

    return error.message || "An unexpected error occurred";
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
};

// Main API functions
export const processNotice = async (
  file: File,
  userInfo: UserInfo,
): Promise<ProcessNoticeResponse> => {
  const formData = new FormData();
  formData.append("pdf_file", file);
  formData.append("user_email", userInfo.user_email);
  formData.append("first_name", userInfo.first_name);
  formData.append("last_name", userInfo.last_name);
  formData.append("company_name", userInfo.company_name);
  formData.append("phone_number", userInfo.phone_number);

  const response = await ocrApiClient.post<ProcessNoticeResponse>(
    "",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const createZendeskTicket = async (
  noticeId: string,
): Promise<ZendeskTicketResponse> => {
  const formData = new FormData();
  formData.append("notice_id", noticeId);

  const response = await zendeskApiClient.post<ZendeskTicketResponse>(
    "",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// Health check functions
export const checkOcrHealth = async (): Promise<HealthCheckResponse> => {
  const response = await ocrApiClient.get<HealthCheckResponse>(
    API_ENDPOINTS.HEALTH_OCR,
  );
  return response.data;
};

export const checkZendeskHealth = async (): Promise<HealthCheckResponse> => {
  const response = await zendeskApiClient.get<HealthCheckResponse>(
    API_ENDPOINTS.HEALTH_ZENDESK,
  );
  return response.data;
};

// Health check with error handling
export const checkAllServices = async (): Promise<{
  ocr: { healthy: boolean; error?: string };
  zendesk: { healthy: boolean; error?: string };
}> => {
  const results = {
    ocr: { healthy: false, error: undefined as string | undefined },
    zendesk: { healthy: false, error: undefined as string | undefined },
  };

  try {
    await checkOcrHealth();
    results.ocr.healthy = true;
  } catch (error) {
    results.ocr.error = handleApiError(error);
  }

  try {
    await checkZendeskHealth();
    results.zendesk.healthy = true;
  } catch (error) {
    results.zendesk.error = handleApiError(error);
  }

  return results;
};
